
import React, { useState, useEffect } from 'react';
import { Report, UnterlageVersion, VorankuendigungArchiveEntry, ReportHistoryEntry } from '../types';
import { Archive, FileClock, FileText, ArrowLeft, Download, ShieldCheck, Calendar, FileSpreadsheet } from 'lucide-react';
import { Button } from './Button';
import { HistoryView } from './HistoryView';
import { generateVorankuendigungPDF } from '../services/pdfService';
import { generateUnterlagePdf } from '../services/pdf/generatorUnterlage';
import { describeVorankuendigungVersion, formatVersionTimestamp, withLiveAppendixImages } from '../services/documentVersioning';
import { fetchReportHistoryList, loadReportSnapshot, deleteReportHistory } from '../services/cloudService';
import { istFreimeldungAktiv } from '../services/freimeldungLogic';
import { PdfPreviewModal } from './PdfPreviewModal';
import { istNativeApp } from '../services/nativePlatform';
import { ausgabeFehlerMelden, dateiAusgeben } from '../services/dateiAusgabe';

interface Props {
    report: Report;
    onBack: () => void;
    projectId?: string;
    /** Nur-Ansicht: Lesen, Vorschau und Download bleiben erlaubt, Schreiben nicht. */
    isReadOnly?: boolean;
}

export const ProjectArchiveView: React.FC<Props> = ({ report, onBack, projectId, isReadOnly = false }) => {
    const [activeTab, setActiveTab] = useState<'inspections' | 'vorank' | 'unterlage'>('inspections');
    const [inspectionHistory, setInspectionHistory] = useState<ReportHistoryEntry[]>([]);
    
    // PDF Preview State
    const [previewReport, setPreviewReport] = useState<Report | null>(null);
    const [showPdfPreview, setShowPdfPreview] = useState(false);

    useEffect(() => {
        const loadHistory = async () => {
            if (projectId && projectId !== 'temp-id') {
                const hist = await fetchReportHistoryList(projectId);
                setInspectionHistory(hist);
            }
        };
        loadHistory();
    }, [projectId]);

    const handleVorankPdf = (entry: VorankuendigungArchiveEntry) => {
        try {
            const doc = generateVorankuendigungPDF(entry.snapshot);
            const filename = `Vorankündigung_${entry.date.split('T')[0]}.pdf`;
            // Android-App: Blob-Downloads kommen in der WebView nicht an → Teilen-Dialog.
            if (istNativeApp()) {
                void dateiAusgeben(doc.output('blob'), filename).catch(ausgabeFehlerMelden);
            } else {
                doc.save(filename);
            }
        } catch (e) {
            console.error(e);
            alert("Fehler beim Generieren der PDF.");
        }
    };

    const handleUnterlagePdf = async (version: UnterlageVersion) => {
        try {
            // Anhangsbilder liegen nicht in der Version (base64 würde das
            // Projekt-JSON aufblähen), sondern im laufenden Dokument.
            const projectData = withLiveAppendixImages(version.projectData, report.unterlage?.projectData?.appendixImages);
            if (istNativeApp()) {
                // Android-App: nur erzeugen (output 'doc'), dann Teilen-Dialog —
                // das doc.save im Generator käme in der WebView nicht an.
                // Dateiname wie im Generator (generatorUnterlage.ts, „SAVE").
                const doc = await generateUnterlagePdf(projectData, version.rabEntries, {
                    showRegulations: true,
                    showTOC: false,
                    orientation: 'portrait',
                    coverImageSize: projectData.coverImageSize || 'medium',
                    output: 'doc'
                });
                const dateiname = `Unterlage_${(projectData.projectName || 'Projekt').replace(/[^a-z0-9äöüß]/gi, '_')}.pdf`;
                await dateiAusgeben(doc.output('blob'), dateiname).catch(ausgabeFehlerMelden);
                return;
            }
            await generateUnterlagePdf(projectData, version.rabEntries, {
                showRegulations: true,
                showTOC: false,
                orientation: 'portrait',
                coverImageSize: projectData.coverImageSize || 'medium'
            });
        } catch (e) {
            console.error(e);
            alert("Fehler beim Generieren der PDF.");
        }
    };

    /**
     * Gleicher Wortlaut wie im Begehungsmodul: Hinweis, konkreter Grund, Ausweg.
     * Ohne den Ausweg sagt die Meldung nur, was nicht geht, aber nicht, wie man
     * in den Schreibmodus kommt.
     */
    const READ_ONLY_HINWEIS = 'Nur-Ansicht: Dieses Projekt ist schreibgeschützt geöffnet — Änderungen werden nicht gespeichert.';
    const READ_ONLY_AUSWEG = 'Zum Bearbeiten das Projekt über die Projektliste öffnen.';
    const readOnlyMessage = (detail: string) => `${READ_ONLY_HINWEIS}\n\n${detail}\n\n${READ_ONLY_AUSWEG}`;

    const handleDeleteHistory = async (id: string) => {
        // Schreibt direkt in die Cloud, also am Lesemodus von
        // App.handleUpdateReport vorbei: der Archiveintrag waere endgueltig weg.
        // HistoryView blendet den Loesch-Knopf im Lesemodus aus und stellt die
        // Rueckfrage gar nicht erst; dieser Riegel bleibt als zweite Reihe.
        if (isReadOnly) {
            alert(readOnlyMessage('Archiveinträge lassen sich in dieser Ansicht nicht löschen.'));
            return;
        }
        const success = await deleteReportHistory(id);
        if (success) {
            setInspectionHistory(prev => prev.filter(h => h.id !== id));
        } else {
            alert("Fehler beim Löschen des Eintrags.");
        }
    };

    return (
        <div data-glass-view="module-archive" className="h-full flex flex-col bg-slate-50 animate-fade-in relative">
            
            {/* Header */}
            <div data-glass="kopf" className="bg-slate-900 text-white p-4 shrink-0 flex items-center justify-between shadow-md z-20">
                <div className="flex items-center gap-4">
                    <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-full transition-colors"><ArrowLeft className="w-5 h-5" /></button>
                    <div>
                         <div className="flex items-center gap-2"><Archive className="w-5 h-5 text-slate-400" /><h2 className="font-bold text-lg leading-tight">Projekt-Archiv</h2></div>
                         <div className="text-xs text-slate-400 mt-1">{report.metadata.projectName}</div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div data-glass="reiter" className="flex border-b border-slate-200 bg-white">
                <button 
                    onClick={() => setActiveTab('inspections')}
                    className={`flex-1 py-4 text-sm font-bold flex items-center justify-center gap-2 border-b-2 transition-colors ${activeTab === 'inspections' ? 'border-blue-600 text-blue-600 bg-blue-50/50' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                >
                    <ShieldCheck className="w-4 h-4"/> Begehungen ({inspectionHistory.length})
                </button>
                <button 
                    onClick={() => setActiveTab('vorank')}
                    className={`flex-1 py-4 text-sm font-bold flex items-center justify-center gap-2 border-b-2 transition-colors ${activeTab === 'vorank' ? 'border-violet-600 text-violet-600 bg-violet-50/50' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                >
                    <Calendar className="w-4 h-4"/> Vorankündigungen ({report.vorankuendigung?.archive?.length || 0})
                </button>
                <button 
                    onClick={() => setActiveTab('unterlage')}
                    className={`flex-1 py-4 text-sm font-bold flex items-center justify-center gap-2 border-b-2 transition-colors ${activeTab === 'unterlage' ? 'border-amber-600 text-amber-600 bg-amber-50/50' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                >
                    <FileSpreadsheet className="w-4 h-4"/> Unterlage
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden relative">
                {activeTab === 'inspections' && (
                    <div className="h-full overflow-hidden p-6">
                        <HistoryView
                            history={inspectionHistory}
                            onLoad={() => {}} // Disabled
                            onDelete={handleDeleteHistory}
                            // Im Lesemodus wird der Snapshot nur gelesen: die
                            // Base64→URL-Selbstheilung schriebe sonst an einem
                            // bereits versendeten Archiveintrag.
                            resolveReport={(id) => loadReportSnapshot(id, projectId, { skipMigration: isReadOnly })}
                            onGeneratePdf={(r) => {
                                setPreviewReport(r);
                                setShowPdfPreview(true);
                            }}
                            currentProjectNumber={report.metadata.projectNumber}
                            settings={report.settings}
                            // Ohne projectId/currentItems blendet die Archivliste
                            // "Mängel wiederherstellen" aus: restoreInspectionItems
                            // schreibt direkt in die Cloud, am Lesemodus vorbei.
                            // Die PDF-Vorschau bleibt unberuehrt, sie laeuft ueber
                            // resolveReport mit der aeusseren projectId.
                            projectId={isReadOnly ? undefined : projectId}
                            // Der Schalter kommt aus dem LEBENDEN Bericht
                            // (`report` ist der aktuelle Projektstand aus
                            // App.tsx), nie aus den Archiv-Snapshots der Liste:
                            // die tragen den Stand vom Versandtag. Nach dem
                            // Abschalten sind die gedruckten QR-Codes tot und
                            // gehoeren in kein Archiv-PDF mehr.
                            freimeldungAktiv={istFreimeldungAktiv(report.metadata)}
                            currentItems={isReadOnly ? undefined : report.items}
                            readOnly={isReadOnly}
                            embedded
                        />
                    </div>
                )}

                {activeTab === 'vorank' && (
                    <div className="h-full overflow-y-auto p-6 max-w-4xl mx-auto">
                        {report.vorankuendigung?.archive && report.vorankuendigung.archive.length > 0 ? (
                            <div className="space-y-4">
                                {report.vorankuendigung.archive.map((entry, idx) => (
                                    <div key={entry.id || idx} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between hover:border-violet-300 transition-colors">
                                        <div className="flex items-center gap-4">
                                            <div className="bg-violet-50 p-3 rounded-lg text-violet-600">
                                                <FileClock className="w-5 h-5"/>
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-slate-800 text-sm">{describeVorankuendigungVersion(entry)}</h4>
                                                <p className="text-xs text-slate-500">{new Date(entry.date).toLocaleDateString('de-DE')} · {new Date(entry.date).toLocaleTimeString('de-DE')} Uhr</p>
                                            </div>
                                        </div>
                                        <Button variant="secondary" onClick={() => handleVorankPdf(entry)} className="text-xs" icon={<Download className="w-4 h-4"/>}>
                                            PDF
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-20 text-slate-400">
                                <Archive className="w-12 h-12 mx-auto mb-3 opacity-20" />
                                <p>Keine archivierten Vorankündigungen vorhanden.</p>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'unterlage' && (
                    <div className="h-full overflow-y-auto p-6 max-w-4xl mx-auto">
                        <div className="bg-amber-50 border border-amber-100 p-4 rounded-xl text-amber-800 text-sm flex items-start gap-3 mb-6">
                             <FileText className="w-5 h-5 shrink-0 mt-0.5" />
                             <p>
                                 Hinweis: Die "Unterlage für spätere Arbeiten" ist ein lebendes Dokument.
                                 Hier stehen die im Modul gespeicherten Versionen — angelegt über "Version speichern"
                                 oder automatisch vor einer Neuanlage. Geladen und gelöscht werden Versionen im Modul selbst.
                             </p>
                        </div>
                        {report.unterlageVersions && report.unterlageVersions.length > 0 ? (
                            <div className="space-y-4">
                                {report.unterlageVersions.map((version, idx) => (
                                    <div key={version.id || idx} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between gap-4 hover:border-violet-300 transition-colors">
                                        <div className="flex items-center gap-4 min-w-0">
                                            <div className="bg-violet-50 p-3 rounded-lg text-violet-600 shrink-0">
                                                <FileSpreadsheet className="w-5 h-5"/>
                                            </div>
                                            <div className="min-w-0">
                                                <h4 className="font-bold text-slate-800 text-sm break-words">{version.label}</h4>
                                                <p className="text-xs text-slate-500">{formatVersionTimestamp(version.createdAt)} Uhr · {version.rabEntries.length} Maßnahmen</p>
                                            </div>
                                        </div>
                                        <Button variant="secondary" onClick={() => handleUnterlagePdf(version)} className="text-xs shrink-0" icon={<Download className="w-4 h-4"/>}>
                                            PDF
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-20 text-slate-400">
                                <Archive className="w-12 h-12 mx-auto mb-3 opacity-20" />
                                <p>Noch keine Version der Unterlage gespeichert.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {showPdfPreview && previewReport && (
                <PdfPreviewModal
                    report={previewReport}
                    settings={report.settings}
                    isReadOnly={isReadOnly}
                    // `previewReport` ist der Archiv-Snapshot; der Schalter
                    // wird deshalb am lebenden `report` gefragt.
                    freimeldungAktiv={istFreimeldungAktiv(report.metadata)}
                    onClose={() => { setShowPdfPreview(false); setPreviewReport(null); }}
                />
            )}
        </div>
    );
};
